import { useState, useCallback } from 'react';
import { usePwaDiagnostics, type PwaDiagnosticCheck } from '@/hooks/usePwaDiagnostics';
import { X, RefreshCw, CheckCircle, XCircle, AlertTriangle, Info, ShieldCheck, Download } from 'lucide-react';
import { clsx } from 'clsx';

/** Merge classes — lightweight replacement for tailwind-merge + clsx */
function cn(...inputs: (string | undefined | false | null)[]) {
  return clsx(...inputs);
}

const STATUS_ICON: Record<PwaDiagnosticCheck['status'], React.ReactNode> = {
  ok: <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />,
  fail: <XCircle className="w-5 h-5 text-red-500 shrink-0" />,
  warn: <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />,
  info: <Info className="w-5 h-5 text-blue-500 shrink-0" />,
  loading: <RefreshCw className="w-5 h-5 text-gray-400 shrink-0 animate-spin" />,
};

const STATUS_LABEL: Record<PwaDiagnosticCheck['status'], string> = {
  ok: 'PASS',
  fail: 'FAIL',
  warn: 'WARN',
  info: 'INFO',
  loading: '...',
};

const STATUS_BG: Record<PwaDiagnosticCheck['status'], string> = {
  ok: 'bg-green-50 border-green-200',
  fail: 'bg-red-50 border-red-200',
  warn: 'bg-amber-50 border-amber-200',
  info: 'bg-blue-50 border-blue-200',
  loading: 'bg-gray-50 border-gray-200',
};

function DiagnosticItem({ check }: { check: PwaDiagnosticCheck }) {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <div className={cn('rounded-lg border p-3 transition-all', STATUS_BG[check.status])}>
      <button
        type="button"
        onClick={() => check.detail && setShowDetail(v => !v)}
        className="flex items-start gap-3 w-full text-left"
      >
        {STATUS_ICON[check.status]}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-gray-900">{check.name}</span>
            <span className={cn(
              'text-[10px] font-bold px-1.5 py-0.5 rounded-full',
              check.status === 'ok' && 'bg-green-100 text-green-700',
              check.status === 'fail' && 'bg-red-100 text-red-700',
              check.status === 'warn' && 'bg-amber-100 text-amber-700',
              check.status === 'info' && 'bg-blue-100 text-blue-700',
              check.status === 'loading' && 'bg-gray-100 text-gray-500',
            )}>
              {STATUS_LABEL[check.status]}
            </span>
          </div>
          <p className="text-sm text-gray-700 mt-0.5">{check.message}</p>
          {check.detail && (
            <p className="text-xs text-gray-500 mt-1">{showDetail ? '▼' : '▶'} Click to {showDetail ? 'hide' : 'show'} details</p>
          )}
        </div>
      </button>
      {showDetail && check.detail && (
        <div className="mt-2 text-xs text-gray-600 bg-white/60 rounded p-2 whitespace-pre-wrap font-mono">
          {check.detail}
        </div>
      )}
    </div>
  );
}

export function PwaDiagnosticsPanel({ onClose }: { onClose: () => void }) {
  const { checks, isLoading, overallStatus, summary, runDiagnostics, deferredPrompt } = usePwaDiagnostics();

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    const result = await deferredPrompt.prompt();
    if (result.outcome === 'accepted') {
      onClose();
    }
  }, [deferredPrompt, onClose]);

  const overallIcon = overallStatus === 'installable' ? '✅' : overallStatus === 'installed' ? '📱' : overallStatus === 'not-installable' ? '❌' : '⏳';
  const overallColor = overallStatus === 'installable' ? 'bg-green-50 text-green-800 border-green-200' : overallStatus === 'installed' ? 'bg-blue-50 text-blue-800 border-blue-200' : overallStatus === 'not-installable' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-amber-50 text-amber-800 border-amber-200';

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-8 pb-8 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-900">PWA Diagnostics</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Overall status */}
          <div className={cn('rounded-lg border p-3 text-sm font-medium', overallColor)}>
            <div className="flex items-center gap-2">
              <span className="text-lg">{overallIcon}</span>
              <span>{summary}</span>
            </div>
            {overallStatus === 'installable' && deferredPrompt && (
              <button
                onClick={handleInstall}
                className="mt-2 flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Install App Now
              </button>
            )}
          </div>

          {/* Checks list */}
          {checks.map((check) => (
            <DiagnosticItem key={check.name} check={check} />
          ))}

          {/* Common causes if not installable */}
          {overallStatus === 'not-installable' && (
            <div className="bg-gray-50 rounded-lg border p-3 text-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Most Common Causes</h4>
              <ul className="list-disc pl-4 space-y-1 text-gray-700">
                <li><strong>Icons 404:</strong> The manifest references icons that don't exist on the server. Check the paths in public/manifest.webmanifest.</li>
                <li><strong>SW not active:</strong> The service worker is registered but waiting. Open DevTools &rarr; Application &rarr; Service Workers &rarr; click "Skip waiting".</li>
                <li><strong>HTTP (not HTTPS):</strong> PWAs require HTTPS (except localhost). Use ngrok or a reverse proxy for testing.</li>
                <li><strong>Already installed:</strong> If the app is already installed, Chrome won't show the prompt again. Uninstall it first.</li>
                <li><strong>Manifest inline conflict:</strong> If VitePWA has an inline manifest, it may override public/manifest.webmanifest with wrong icon paths.</li>
              </ul>
            </div>
          )}

          {/* Chrome DevTools tip */}
          <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-3 text-sm text-indigo-800">
            <strong>💡 Pro tip:</strong> Open Chrome DevTools &rarr; Application &rarr; Manifest to see the parsed manifest and any icon errors. Then check Lighthouse &rarr; PWA for a full audit.
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={runDiagnostics}
            disabled={isLoading}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
            {isLoading ? 'Running...' : 'Run Diagnostics'}
          </button>
        </div>
      </div>
    </div>
  );
}
